/**
 * useSynapseWorker - v1.0
 * React hook for interacting with the Synapse worker
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { synapseWorkerBridge } from '../lib/SynapseWorkerBridge';
import type {
  WorkerResponse,
  WorkerError,
  WorkerStatus,
  MessageType,
  Task,
  TaskEvent,
  HealthCheckResponse,
  StatusResponse,
  TasksListResponse,
  TaskGetResponse,
  TaskSubmitResponse,
  MetricsResponse,
  ConfigResponse,
  ClaudeConfigResponse,
  VoiceTasksResponse,
} from '../types/worker-messages';
import { isWorkerError } from '../types/worker-messages';

// ============================================================================
// Types
// ============================================================================

interface UseSynapseWorkerResult {
  // Status
  status: WorkerStatus;
  isReady: boolean;
  isConnected: boolean;

  // Send raw message
  send: <T = any>(type: MessageType, payload?: any) => Promise<WorkerResponse<T>>;

  // Typed API methods
  api: {
    // Health & Status
    checkHealth: () => Promise<HealthCheckResponse>;
    getStatus: () => Promise<StatusResponse>;

    // Tasks
    listTasks: (limit?: number) => Promise<Task[]>;
    getTask: (taskId: string) => Promise<TaskGetResponse>;
    submitTask: (description: string, priority?: 'LOW' | 'NORMAL' | 'HIGH') => Promise<TaskSubmitResponse>;
    retryTask: (taskId: string) => Promise<{ success: boolean; taskId: string }>;

    // Metrics
    getMetrics: () => Promise<MetricsResponse>;

    // Config
    getConfig: () => Promise<ConfigResponse>;
    updateConfig: (config: Record<string, unknown>) => Promise<ConfigResponse>;
    getSynapseConfig: () => Promise<ConfigResponse>;

    // Control
    pausePipeline: () => Promise<{ success: boolean }>;
    resumePipeline: () => Promise<{ success: boolean }>;

    // Claude Config
    getClaudeConfig: (file: 'personality' | 'snippets' | 'memory') => Promise<string>;
    saveClaudeConfig: (file: 'personality' | 'snippets' | 'memory', content: string) => Promise<{ success: boolean }>;

    // Voice
    listVoiceTasks: () => Promise<Task[]>;
    submitVoiceTask: (query: string, priority?: 'low' | 'normal' | 'high') => Promise<TaskSubmitResponse>;

    // File upload
    uploadFile: (filename: string, destination: string, content: string, encoding?: 'base64' | 'utf-8') => Promise<{ success: boolean }>;

    // Feedback
    listFeedback: () => Promise<any[]>;
    submitFeedback: (project: string, message: string, email?: string) => Promise<{ success: boolean }>;

    // Deploy
    deploy: (taskId: string, options?: Record<string, unknown>) => Promise<{ success: boolean }>;
  };

  // Subscriptions
  subscribe: (type: 'tasks' | 'task', callback: (event: TaskEvent) => void, taskId?: string) => () => void;

  // Error from last operation
  lastError: WorkerError | null;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useSynapseWorker(): UseSynapseWorkerResult {
  const [status, setStatus] = useState<WorkerStatus>(synapseWorkerBridge.status);
  const [lastError, setLastError] = useState<WorkerError | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const subscriptionsRef = useRef<Array<() => void>>([]);

  // Initialize worker on mount
  useEffect(() => {
    synapseWorkerBridge.initialize().catch((err) => {
      console.error('[useSynapseWorker] Failed to initialize:', err);
    });

    // Listen for status changes
    const unsubStatus = synapseWorkerBridge.onStatusChange(setStatus);

    // Listen for WebSocket connection events
    const unsubEvents = synapseWorkerBridge.onEvent((event) => {
      if (event.type === 'ws:connected') {
        setIsConnected(true);
      } else if (event.type === 'ws:disconnected') {
        setIsConnected(false);
      }
    });

    return () => {
      unsubStatus();
      unsubEvents();
      // Cleanup subscriptions
      subscriptionsRef.current.forEach((unsub) => unsub());
    };
  }, []);

  // Raw send function
  const send = useCallback(async <T = any>(
    type: MessageType,
    payload: any = {}
  ): Promise<WorkerResponse<T>> => {
    setLastError(null);
    try {
      const response = await synapseWorkerBridge.send<T>(type, payload);
      if (response.type === 'error' && isWorkerError(response.payload)) {
        setLastError(response.payload);
      }
      return response;
    } catch (error) {
      const workerError: WorkerError = {
        code: 'WORKER_UNAVAILABLE',
        message: error instanceof Error ? error.message : 'Unknown error',
        retryable: true,
      };
      setLastError(workerError);
      throw error;
    }
  }, []);

  // Helper to extract payload or throw error
  const extractPayload = <T>(response: WorkerResponse<T>): T => {
    if (response.type === 'error') {
      throw new Error((response.payload as unknown as WorkerError).message);
    }
    return response.payload;
  };

  // Typed API methods
  const api = {
    // Health & Status
    checkHealth: async () => {
      const response = await send<HealthCheckResponse>('health:check');
      return extractPayload(response);
    },

    getStatus: async () => {
      const response = await send<StatusResponse>('status:get');
      return extractPayload(response);
    },

    // Tasks
    listTasks: async (limit?: number) => {
      const response = await send<TasksListResponse>('tasks:list', { limit });
      return extractPayload(response).tasks;
    },

    getTask: async (taskId: string) => {
      const response = await send<TaskGetResponse>('task:get', { taskId });
      return extractPayload(response);
    },

    submitTask: async (description: string, priority: 'LOW' | 'NORMAL' | 'HIGH' = 'NORMAL') => {
      const response = await send<TaskSubmitResponse>('task:submit', { description, priority });
      return extractPayload(response);
    },

    retryTask: async (taskId: string) => {
      const response = await send<{ success: boolean; taskId: string }>('task:retry', { taskId });
      return extractPayload(response);
    },

    // Metrics
    getMetrics: async () => {
      const response = await send<MetricsResponse>('metrics:get');
      return extractPayload(response);
    },

    // Config
    getConfig: async () => {
      const response = await send<ConfigResponse>('config:get');
      return extractPayload(response);
    },

    updateConfig: async (config: Record<string, unknown>) => {
      const response = await send<ConfigResponse>('config:update', { config });
      return extractPayload(response);
    },

    getSynapseConfig: async () => {
      const response = await send<ConfigResponse>('config:synapse');
      return extractPayload(response);
    },

    // Control
    pausePipeline: async () => {
      const response = await send<{ success: boolean }>('control:pause');
      return extractPayload(response);
    },

    resumePipeline: async () => {
      const response = await send<{ success: boolean }>('control:resume');
      return extractPayload(response);
    },

    // Claude Config
    getClaudeConfig: async (file: 'personality' | 'snippets' | 'memory') => {
      const response = await send<ClaudeConfigResponse>('claude:config:get', { file });
      return extractPayload(response).content;
    },

    saveClaudeConfig: async (file: 'personality' | 'snippets' | 'memory', content: string) => {
      const response = await send<{ success: boolean }>('claude:config:save', { file, content });
      return extractPayload(response);
    },

    // Voice
    listVoiceTasks: async () => {
      const response = await send<VoiceTasksResponse>('voice:tasks:list');
      return extractPayload(response).tasks;
    },

    submitVoiceTask: async (query: string, priority: 'low' | 'normal' | 'high' = 'high') => {
      const response = await send<TaskSubmitResponse>('voice:task:submit', { query, priority });
      return extractPayload(response);
    },

    // File upload
    uploadFile: async (
      filename: string,
      destination: string,
      content: string,
      encoding: 'base64' | 'utf-8' = 'base64'
    ) => {
      const response = await send<{ success: boolean }>('file:upload', {
        filename,
        destination,
        content,
        encoding,
      });
      return extractPayload(response);
    },

    // Feedback
    listFeedback: async () => {
      const response = await send<{ feedback: any[] }>('feedback:list');
      return extractPayload(response).feedback;
    },

    submitFeedback: async (project: string, message: string, email?: string) => {
      const response = await send<{ success: boolean }>('feedback:submit', {
        project,
        message,
        email,
      });
      return extractPayload(response);
    },

    // Deploy
    deploy: async (taskId: string, options?: Record<string, unknown>) => {
      const response = await send<{ success: boolean }>('deploy:execute', { taskId, options });
      return extractPayload(response);
    },
  };

  // Subscription function
  const subscribe = useCallback(
    (type: 'tasks' | 'task', callback: (event: TaskEvent) => void, taskId?: string): (() => void) => {
      let unsubscribeFn: (() => void) | null = null;

      synapseWorkerBridge
        .subscribe(type, callback, taskId)
        .then((subscription) => {
          unsubscribeFn = subscription.unsubscribe;
          subscriptionsRef.current.push(subscription.unsubscribe);
        })
        .catch((err) => {
          console.error('[useSynapseWorker] Subscribe failed:', err);
        });

      return () => {
        if (unsubscribeFn) {
          unsubscribeFn();
          subscriptionsRef.current = subscriptionsRef.current.filter((fn) => fn !== unsubscribeFn);
        }
      };
    },
    []
  );

  return {
    status,
    isReady: status === 'ready',
    isConnected,
    send,
    api,
    subscribe,
    lastError,
  };
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Hook for subscribing to all task events
 */
export function useTaskSubscription(callback: (event: TaskEvent) => void): void {
  const { subscribe } = useSynapseWorker();

  useEffect(() => {
    return subscribe('tasks', callback);
  }, [subscribe, callback]);
}

/**
 * Hook for subscribing to a specific task's events
 */
export function useSingleTaskSubscription(
  taskId: string | null,
  callback: (event: TaskEvent) => void
): void {
  const { subscribe } = useSynapseWorker();

  useEffect(() => {
    if (!taskId) return;
    return subscribe('task', callback, taskId);
  }, [subscribe, taskId, callback]);
}

/**
 * Hook for fetching tasks with automatic refresh
 */
export function useTasks(options?: { limit?: number; refreshInterval?: number }): {
  tasks: Task[];
  loading: boolean;
  error: WorkerError | null;
  refresh: () => Promise<void>;
} {
  const { api, lastError, isReady } = useSynapseWorker();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!isReady) return;
    setLoading(true);
    try {
      const result = await api.listTasks(options?.limit);
      setTasks(result);
    } catch {
      // Error is captured in lastError
    } finally {
      setLoading(false);
    }
  }, [api, isReady, options?.limit]);

  useEffect(() => {
    refresh();

    if (options?.refreshInterval) {
      const interval = setInterval(refresh, options.refreshInterval);
      return () => clearInterval(interval);
    }
  }, [refresh, options?.refreshInterval]);

  return { tasks, loading, error: lastError, refresh };
}

/**
 * Hook for health status
 */
export function useHealth(): {
  isOnline: boolean | null;
  version: string;
  lastChecked: Date | null;
} {
  const { api, isReady } = useSynapseWorker();
  const [isOnline, setIsOnline] = useState<boolean | null>(null);
  const [version, setVersion] = useState('');
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  useEffect(() => {
    if (!isReady) return;

    const checkHealth = async () => {
      try {
        const result = await api.checkHealth();
        setIsOnline(result.status === 'healthy');
        setVersion(result.version);
        setLastChecked(new Date());
      } catch {
        setIsOnline(false);
      }
    };

    checkHealth();
    const interval = setInterval(checkHealth, 30000); // Health check every 30s, not 5s
    return () => clearInterval(interval);
  }, [api, isReady]);

  return { isOnline, version, lastChecked };
}
